import { ObserverModel } from 'projects/core/src/lib/models/observer.model';
import { TranslocoService } from '@ngneat/transloco';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { RoleModel } from 'projects/core/src/lib/models/role.model';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { SelectItem } from 'primeng/api';
import { OrderformParticipantsAmounttype, OrderformParticipantsConfig, OrderformParticipantsFormmode } from './order-detail-participants.config';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { Subscription } from 'rxjs';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { SALUTATIONS } from 'projects/core/src/lib/constants';
import { EmployeeSearchComponent } from 'projects/admin/src/app/shared/employee-search/employee-search.component';
import { EmployeeModel } from 'projects/core/src/lib/models/employee.model';
import { faCommentSlash } from '@fortawesome/pro-light-svg-icons';
import { DetailModes } from 'projects/admin/src/app/shared/constants';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { UserAppointments, UserLocations } from '../../../move-order/move-order.component';
import { faCheckCircle, faDoNotEnter, faExclamationCircle, faMapMarkerTimes } from '@fortawesome/pro-solid-svg-icons';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import moment from 'moment';
import { SelectedSuggestion } from '../../../move-order/partials/move-order-suggestions/move-order-suggestions.types';

const ROLE_ORDER = ['moderator', 'moderator_external', 'observer', 'observer_gem'];
const IS_FOR_RUNNER = true;

enum EditMode {
  Create = 'create',
  Edit = 'edit',
}

enum DeleteResourceMode {
  Keep = 'keep',
  Delete = 'delete',
}

enum RoleType {
  Moderator = 'moderator',
  Observer = 'observer',
  ObserverGem = 'observer_gem',
  Orderer = 'orderer',
  Other = 'other',
}

class CustomUserModel extends UserModel {
  editMode: EditMode;
  role: RoleModel;
  silentobserver: boolean;
}

@Component({
  selector: 'app-order-detail-participants',
  templateUrl: './order-detail-participants.component.html',
  styleUrls: ['./order-detail-participants.component.scss']
})
export class OrderDetailParticipantsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<any> };
  @Input() appointments: UserAppointments;
  @Input() userLocations: UserLocations;
  @Input() settings: { [key: string]: any };
  @Input() texts: { [key: string]: string };
  @Input() order: AcModel = null;
  @Input() mode: DetailModes | null;
  @Input() inEdit: boolean;
  @Input() submitting: boolean;

  @Input() move: boolean;
  @Input() movingDate: Date | null = null;
  @Input() movingLocation: LocationModel | null = null;

  @Output() sendInvitation = new EventEmitter<any>();

  @ViewChild('employeesearch') employeesearch: EmployeeSearchComponent;

  public users: Array<CustomUserModel> = [];
  public config = OrderformParticipantsConfig;
  public dialogVisible = false;
  public dialogMode: EditMode;
  public dialogUserId: number = null;
  public dialogRoleId: number = null;
  public formMode: OrderformParticipantsFormmode = null;
  public showSilentObserver = false;

  public deleteDialogVisible = false;
  public deleteDialogUser: CustomUserModel | null = null;

  public faCommentSlash = faCommentSlash;
  public faAvailable = faCheckCircle;
  public faNotAvailable = faExclamationCircle;
  public faBlocked = faDoNotEnter;
  public faMapMarkerTimes = faMapMarkerTimes;

  public participantForm: UntypedFormGroup = this.fb.group({
    role: new UntypedFormControl('', [Validators.required]),
    user: new UntypedFormControl(''),
    silentobserver: new UntypedFormControl(''),
    gender: new UntypedFormControl(''),
    firstname: new UntypedFormControl(''),
    lastname: new UntypedFormControl(''),
    email: new UntypedFormControl('', [CustomValidators.email()]),
    phone: new UntypedFormControl(''),
    mobile: new UntypedFormControl(''),
  });
  public optionsSalutations: Array<SelectItem> = [];
  public optionsRoles: Array<RoleModel> = [];
  public optionsUsers: Array<UserModel> = [];

  public deleteForm: UntypedFormGroup = this.fb.group({
    resourcemode: new UntypedFormControl('')
  });
  public optionsResourceModes: Array<any> = [
    {
      value: DeleteResourceMode.Keep,
      label: this.transloco.translate('order_detail.keep_availability')
    },
    {
      value: DeleteResourceMode.Delete,
      label: this.transloco.translate('order_detail.delete_availability')
    },
  ];
  public toDelete: Array<string> = [];

  public formModes = OrderformParticipantsFormmode;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private transloco: TranslocoService,
    private helper: HelperService,
    private ui: UiService,
    public auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.optionsSalutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
    this.initSubscriptions();
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.order || changes.inEdit) {
      this.initData();
    }
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public addParticipant(): void {
    if (this.inEdit === true && this.canEditParticipants() === true) {
      this.setRoles();
      this.dialogMode = EditMode.Create;
      this.dialogVisible = true;
      this.dialogUserId = null;
      this.dialogRoleId = null;
      this.resetForm();
      this.participantForm.get('role').enable();
    }
  }

  public editParticipant(user: CustomUserModel): void {
    if (this.inEdit === true && this.canEditParticipants() === true) {
      this.setRoles(user.role.id);
      const role = this.optionsRoles.find(actual => actual.id === user.role.id);
      if (role !== undefined) {
        // init
        this.dialogMode = EditMode.Edit;
        this.dialogVisible = true;
        this.dialogUserId = user.id;
        this.dialogRoleId = role.id;
        this.resetForm();
        this.participantForm.get('role').disable();
        // set data
        this.participantForm.get('role').setValue(role);
        this.participantForm.get('silentobserver').setValue(user.silentobserver);
        setTimeout(() => {
          if (this.formMode === OrderformParticipantsFormmode.Select) {
            this.participantForm.patchValue({
              user: this.optionsUsers.find(actualUser => actualUser.id === user.id) || ''
            });
          } else {
            this.participantForm.patchValue({
              gender: this.optionsSalutations.find(gender => {
                return gender.value === user.gender;
              })?.value,
              firstname: user.firstname,
              lastname: user.lastname,
              email: user.email,
              phone: user.phone,
              mobile: user.mobile,
            });
          }
        }, 100);
      }
    }
  }

  public cancelParticipant(): void {
    if (this.inEdit === true) {
      this.closeAndClearDialog();
    }
  }

  public saveParticipant(): void {
    if (this.inEdit === true) {
      if (this.participantForm.valid === true) {
        let changeModeToCreate = false;
        let user = this.users.find((actualUser: CustomUserModel) => {
          return actualUser.id === this.dialogUserId && actualUser.role.id === this.dialogRoleId;
        }) || null;
        if (
          this.dialogMode === EditMode.Edit &&
          (
            this.formMode === OrderformParticipantsFormmode.Form &&
            this.participantForm.get('email').value !== user?.email
          ) ||
          (
            this.formMode === OrderformParticipantsFormmode.Select &&
            this.participantForm.get('user').value?.id !== user?.id
          )
        ) {
          changeModeToCreate = true;
        }
        let data = {};
        if (this.formMode === OrderformParticipantsFormmode.Select) {
          const formUser = this.participantForm.get('user').value;
          const role = this.participantForm.get('role').value;
          if (formUser) {
            data = {
              role,
              silentobserver: this.participantForm.get('silentobserver').value,
              id: formUser.id,
              gender: formUser.gender,
              firstname: formUser.firstname,
              lastname: formUser.lastname,
              email: formUser.email,
              phone: formUser.phone,
              mobile: formUser.mobile,
              temp: ROLE_ORDER.includes(role?.name) ? IS_FOR_RUNNER : false,
            };
          }
        } else {
          data = {
            role: this.participantForm.get('role').value,
            silentobserver: this.participantForm.get('silentobserver').value,
            gender: this.participantForm.get('gender').value,
            firstname: this.participantForm.get('firstname').value,
            lastname: this.participantForm.get('lastname').value,
            email: this.participantForm.get('email').value,
            phone: this.participantForm.get('phone').value,
            mobile: this.participantForm.get('mobile').value,
          };
        }
        // create or update user
        if (this.dialogMode === EditMode.Create) {
          user = new CustomUserModel().deserialize({...data, editMode: EditMode.Create});
          this.users.push(user);
        } else if (user !== null) {
          if (changeModeToCreate === true) {
            user.deserialize({...data, editMode: EditMode.Create});
          } else {
            user.deserialize(data);
          }
        }
        this.sortUsers();
        // set silent observer flag for all identical users in list
        const silentobserver = user.silentobserver;
        this.users.forEach((actual: CustomUserModel) => {
          if (actual.email.toLocaleLowerCase() === user.email.toLocaleLowerCase()) {
            actual.silentobserver = silentobserver;
          }
        });
        this.closeAndClearDialog();
      } else {
        FormHelper.markFormAsDirty(this.participantForm);
      }
    }
  }

  public onStartDeleteParticipant(user: CustomUserModel): void {
    if (this.inEdit === true && this.users && this.canEditParticipants() === true) {
      this.deleteDialogVisible = true;
      this.deleteDialogUser = user;
      this.deleteForm.get('resourcemode').setValue(DeleteResourceMode.Keep);
    }
  }

  public hasAvailabilities(): boolean {
    return this.deleteDialogUser?.role?.name === 'moderator' ||
      this.deleteDialogUser?.role?.name === 'moderator_external' ||
      this.deleteDialogUser?.role?.name === 'observer' ||
      this.deleteDialogUser?.role?.name === 'observer_gem'
  }

  public deleteParticipant(): void {
    this.users = this.users.filter((actualUser: CustomUserModel) => {
      return actualUser.id !== this.deleteDialogUser.id || actualUser.role.id !== this.deleteDialogUser.role.id;
    });
    this.sortUsers();
    if (
      this.hasAvailabilities() &&
      this.deleteDialogUser.hashed_id
    ) {
      if (this.deleteForm.get('resourcemode').value === DeleteResourceMode.Delete) {
        this.toDelete.push(this.deleteDialogUser.hashed_id);
      } else if (this.deleteForm.get('resourcemode').value === DeleteResourceMode.Keep) {
        this.toDelete = this.toDelete.filter((id: string) => {
          return id !== this.deleteDialogUser.hashed_id;
        });
      }
    }
    this.cancelDeleteParticipant();
  }

  public cancelDeleteParticipant(): void {
    this.deleteDialogVisible = false;
    this.deleteDialogUser = null;
  }

  public getPayload(): any {
    return {
      users: this.users.map((user: CustomUserModel) => {
        return {
          role: user.role?.name,
          mode: user.editMode,
          id: (user.id) ? user.id : null,
          silentobserver: user.silentobserver,
          gender: user.gender,
          firstname: user.firstname,
          lastname: user.lastname,
          email: user.email,
          phone: user.phone,
          mobile: user.mobile,
        };
      }),
      delete: this.toDelete
    };
  }

  public canResendEmail(): boolean {
    return this.hasStatus(['planned', 'running']);
  }

  public canEditParticipants(): boolean {
    return this.mode === DetailModes.new || this.hasStatus(['requested', 'planned']);
  }

  public showSubmitMessages(messages: any): void {
    const alertMessages = [];
    // not changed usersdata
    if (Array.isArray(messages.participants?.not_changed_usersdata)) {
      let users = this.users.filter((user: CustomUserModel) => {
        return messages.participants.not_changed_usersdata.includes(user.id);
      }).map((user: CustomUserModel) => user.fullname );
      const usersList = users.filter((value, index, array) => {
        return array.indexOf(value) === index;
      }).join(', ');
      alertMessages.push(
        this.transloco.translate('freeorder.not_changed_usersdata', { users: usersList })
      );
    }
    // appointments in use
    if (Array.isArray(messages.participants?.appointment_in_use) && messages.participants?.appointment_in_use.length > 0) {
      const ordernumberList = messages.participants?.appointment_in_use.join(', ');
      alertMessages.push(
        this.transloco.translate('freeorder.appointment_in_use', { ordernumbers: ordernumberList })
      );
    }
    // get message
    if (alertMessages.length > 0) {
      this.ui.showAlert(alertMessages.join('<hr/>'));
    }
  }

  private hasStatus(list: Array<string>): boolean {
    return list.find(status => status === this.order?.status) !== undefined;
  }

  private initData(): void {
    this.users = [];
    if (this.order && this.options?.roles) {
      // set moderator and users
      this.users = [
        ...this.getModerators(),
        ...this.getOrderer(),
        ...this.getUsers(),
      ];
      // afterwards add observers
      this.users = [
        ...this.users,
        ...this.getObservers()
      ];
    }
    this.sortUsers();
    this.toDelete = [];
  }

  private sortUsers(): void {
    this.users.sort((a: CustomUserModel, b: CustomUserModel) => {
      let roleA: number = ROLE_ORDER.indexOf(a.role.name);
      let roleB: number = ROLE_ORDER.indexOf(b.role.name);
      if (roleA === -1) roleA = Infinity;
      if (roleB === -1) roleB = Infinity;
      if (roleA === Infinity && roleB === Infinity) {
        return this.helper.getSortValue(a.role.translated_rolename, b.role.translated_rolename);
      }
      if (roleA !== roleB) {
        return roleA - roleB;
      }
    });
  }

  private getUsers(): Array<CustomUserModel> {
    return this.order.users.map((user: UserModel) => {
      const newUser = new CustomUserModel().deserialize({...user});
      const role = this.getRoleFromOptions(user.pivot.role_id, 'id');
      newUser.role = role;
      newUser.editMode = EditMode.Edit;
      return newUser;
    });
  }

  private getModerators(): Array<CustomUserModel> {
    if (this.order.moderator) {
      const moderator = new CustomUserModel().deserialize(this.order.moderator);
      moderator.editMode = EditMode.Edit;
      moderator.temp = IS_FOR_RUNNER;
      moderator.role = this.getRoleFromUser(moderator, RoleType.Moderator);
      if (!moderator.role) {
        const pseudoRole = new RoleModel().deserialize({
          name: 'moderator'
        });
        pseudoRole.setTranslocoService(this.transloco);
        moderator.role = pseudoRole;
      }
      return [moderator];
    }
    return [];
  }

  private getOrderer(): Array<CustomUserModel> {
    if (this.order.orderer) {
      const orderer = new CustomUserModel().deserialize(this.order.orderer);
      orderer.editMode = EditMode.Edit;
      orderer.role = this.getRoleFromOptions(RoleType.Orderer, 'name');
      return [orderer];
    }
    return [];
  }

  private getObservers(): Array<CustomUserModel> {
    return this.order.observers.filter((observer: ObserverModel) => {
      if (observer.pivot?.type === 'active') {
        // show if active user
        return true;
      } else {
        const usersInList = this.users.filter(actual => actual.id === observer.id);
        if (usersInList.length === 0) {
          // show if silent but not as user in this.users
          return true;
        } else {
          // set flag to existing user
          usersInList.forEach(user => user.silentobserver = true);
          return false;
        }
      }
    }).map((observer: ObserverModel) => {
      const newObserver = new CustomUserModel().deserialize({...observer});
      newObserver.editMode = EditMode.Edit;
      newObserver.silentobserver = observer.pivot?.type === 'silent';
      newObserver.role = this.getRoleFromUser(newObserver, (observer.pivot?.executive === true) ? RoleType.ObserverGem : RoleType.Observer);
      newObserver.temp = IS_FOR_RUNNER;
      if (newObserver.role === null) {
        newObserver.role = this.getRoleFromOptions((newObserver.silentobserver !== true) ? RoleType.Observer : RoleType.Other, 'name');
      }
      return newObserver;
    });
  }

  private getRoleFromOptions(key: any, type: string): RoleModel | null {
    const roleData = this.options?.roles.find((actualRole: any) => {
      return (type === 'id' && key === actualRole.id) || (type === 'name' && key === actualRole.name);
    });
    if (roleData !== undefined) {
      const role = new RoleModel().deserialize(roleData);
      if (role !== undefined) {
        role.setTranslocoService(this.transloco);
        return role;
      }
    }
    return null;
  }

  private getRoleFromUser(user: CustomUserModel, type: RoleType): RoleModel | null {
    const role = user.roles?.find((actualRole: RoleModel) => {
      actualRole.setTranslocoService(this.transloco);
      return actualRole.name.includes(type);
    });
    return role || null;
  }

  public onOpenEmployeeSearch(): void {
    this.employeesearch.open();
  }

  public onSelectEmployee(employee: EmployeeModel): void {
    this.participantForm.patchValue({
      gender: this.optionsSalutations.find(gender => gender.value[0] === employee.gender)?.value,
      firstname: employee.firstname,
      lastname: employee.lastname,
      email: employee.email,
      phone: employee.phone,
      mobile: employee.mobile,
    });
  }

  public onResendInvitation(role: RoleModel, user: UserModel, mode: string = null): void {
    this.sendInvitation.emit({
      role: role.name,
      id: user.hashed_id,
      name: user.fullname,
      mode
    });
  }

  public hasInvitationRole(participant: CustomUserModel): boolean {
    return ['moderator', 'moderator_external', 'observer', 'observer_gem'].includes(participant.role?.name) || participant.silentobserver === true;
  }

  public hasInfomailRole(participant: CustomUserModel): boolean {
    return ['workers_council', 'hr_businesspartner'].includes(participant.role?.name);
  }

  private closeAndClearDialog(): void {
    this.dialogVisible = false;
    this.dialogUserId = null;
    this.dialogRoleId = null;
    this.clearForm();
    this.resetForm();
  }

  private clearForm(): void {
    this.participantForm.patchValue({
      role: '',
      user: '',
      silentobserver: '',
      gender: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      mobile: '',
    });
  }

  private initSubscriptions(): void {
    this.subscriptions.push(this.participantForm.get('role').valueChanges.subscribe(() => {
      this.setFormSettings();
    }));
  }

  private setRoles(roleId: number = null): void {
    // set options
    this.optionsRoles = this.options?.roles.map((actualRole: any) => {
      const role = new RoleModel().deserialize(actualRole);
      role.setTranslocoService(this.transloco);
      return role;
    }).filter((role: RoleModel) => {
      const config = OrderformParticipantsConfig[role.name];
      if (config) {
        return config.amount === OrderformParticipantsAmounttype.Multiple || this.hasRoleInList(role) === false || role.id === roleId;
      } else if (role.name === 'moderator_external') {
        return this.hasRoleInList(role) === false || role.id === roleId;
      }
    }).sort((a: RoleModel, b: RoleModel) => {
      return this.helper.getSortValue(a.translated_rolename, b.translated_rolename);
    });
    // reduce to mod/obs in move mode
    if (this.isMoveMode() === true) {
      this.optionsRoles = this.optionsRoles.filter((role: RoleModel) => {
        return ROLE_ORDER.includes(role.name);
      });
    }
  }

  private setFormSettings(): void {
    // set form settings
    const role = this.participantForm.get('role').value;
    if (role) {
      const config = OrderformParticipantsConfig[role.name];
      if (config) {
        this.formMode = config.mode;
        this.showSilentObserver = config.silentobserver;
      } else if (role.name === 'moderator_external') {
        this.formMode = OrderformParticipantsFormmode.Select;
        this.showSilentObserver = false;
      }
      if (this.formMode === OrderformParticipantsFormmode.Select) {
        this.optionsUsers = this.options?.users.map((user: any) => {
          return new UserModel().deserialize(user);
        }).filter((user: UserModel) => {
          return this.userShouldBeInOptionlist(user, role);
        }).sort((a: UserModel, b: UserModel) => {
          return this.helper.getSortValue(a.lastname, b.lastname);
        });
      } else {
        this.optionsUsers = [];
      }
    }
    this.setValidators();
  }

  private userShouldBeInOptionlist(user: UserModel, role: RoleModel): boolean {
    if (this.dialogMode === EditMode.Edit && this.dialogUserId !== null) {
      // EDIT: show users with this role but not in list (except user to edit in form)
      return user.roles.find((actual: RoleModel) => actual.id === role.id) !== undefined &&
        this.users.find((actual: CustomUserModel) => actual.email === user.email && actual.id !== this.dialogUserId) === undefined;
    } else if (this.dialogMode === EditMode.Create) {
      // CREATE: show users with this role but not in list with same role
      return user.roles.find((actual: RoleModel) => actual.id === role.id) !== undefined &&
        this.users.find((actual: CustomUserModel) => actual.email === user.email && actual.role?.id === role.id) === undefined;
    }
    return false;
  }

  private setValidators(): void {
    if (this.formMode === OrderformParticipantsFormmode.Select) {
      this.participantForm.get('user').setValidators([Validators.required]);
      this.participantForm.get('gender').clearValidators();
      this.participantForm.get('firstname').clearValidators();
      this.participantForm.get('lastname').clearValidators();
      this.participantForm.get('email').clearValidators();
    } else {
      this.participantForm.get('user').clearValidators();
      this.participantForm.get('gender').setValidators([Validators.required]);
      this.participantForm.get('firstname').setValidators([Validators.required]);
      this.participantForm.get('lastname').setValidators([Validators.required]);
      this.participantForm.get('email').setValidators([Validators.required, CustomValidators.email()]);
    }
    this.participantForm.get('user').updateValueAndValidity();
    this.participantForm.get('gender').updateValueAndValidity();
    this.participantForm.get('firstname').updateValueAndValidity();
    this.participantForm.get('lastname').updateValueAndValidity();
    this.participantForm.get('email').updateValueAndValidity();
  }

  private resetForm(): void {
    this.formMode = null;
    this.participantForm.reset();
    this.participantForm.markAsUntouched();
    this.participantForm.markAsPristine();
  }

  private hasRoleInList(role: RoleModel): boolean {
    let result = false;
    this.users.forEach((user: CustomUserModel) => {
      if (role.name.includes('moderator') && user.role?.name.includes('moderator')) {
        result = true;
      } else if (user.role?.id === role.id) {
        result = true;
      }
    });
    return result;
  }

  // FUNCTIONS FOR MOVING MODE

  public isMoveMode(): boolean {
    return this.move === true;
  }

  public getAppointmentInfoType(participant: UserModel): string | null {
    if (this.isMoveMode() && participant.temp) {
      // check location
      const loc = this.getActualLocation();
      if (!Array.isArray(this.userLocations[participant.id]) || !this.userLocations[participant.id].includes(loc.id)) {
        return 'location';
      }
      // check appointment
      if (this.appointments !== null) {
        const appointment = this.appointments[participant.id]?.find((actual: any) => {
          return actual.day_as_day === this.getActualDay();
        }) || null;
        if (appointment !== null) {
          let acIds = appointment.used_by_ac || [];
          acIds = acIds.filter((id: number) => {
            return id !== this.order.id;
          });
          if (acIds.length === 0) {
            return 'available';
          } else {
            return 'blocked';
          }
        }
      }
    }
    return 'not-available';
  }

  public isBlocked(): boolean {
    return this.users.filter((user: UserModel) => {
      return user.temp === IS_FOR_RUNNER && (
        this.getAppointmentInfoType(user) === 'blocked'
      );
    }).length > 0;
  }

  private getActualDay(): string {
    return moment((this.movingDate) ? this.movingDate : this.order.start_time).format('YYYY-MM-DD');
  }

  private getActualLocation(): LocationModel {
    return (this.movingLocation) ? this.movingLocation : this.order.location;
  }

  public copyDirectLink(user: UserModel): void {
    if (this.auth.isSuperUser()) {
      this.ui.copyToClipboard(user.directlink);
    }
  }

  public setSuggestion(suggestion: SelectedSuggestion): void {
    // delete old mod and obs
    this.users = this.users.filter((user: CustomUserModel) => {
      return user.temp !== IS_FOR_RUNNER;
    });
    // add users from suggestion
    const moderatorRole = suggestion.moderator?.roles[0] || null;
    if (moderatorRole !== null) {
      moderatorRole.setTranslocoService(this.transloco);
      this.users.push(new CustomUserModel().deserialize({...suggestion.moderator, role: moderatorRole, temp: IS_FOR_RUNNER}));
    }
    suggestion.observers?.forEach((observer: UserModel) => {
      const observerRole = observer.roles[0] || null;
      if (observerRole !== null) {
        observerRole.setTranslocoService(this.transloco);
        this.users.push(new CustomUserModel().deserialize({...observer, role: observerRole, temp: IS_FOR_RUNNER}));
      }
    });
    suggestion.observers_gem?.forEach((observer: UserModel) => {
      const observerRole = observer.roles.find(role => role.name.includes('gem')) || null;
      if (observerRole !== null) {
        observerRole.setTranslocoService(this.transloco);
        this.users.push(new CustomUserModel().deserialize({...observer, role: observerRole, temp: IS_FOR_RUNNER}));
      }
    });
    this.sortUsers();
  }

  public hasEnoughParticipants(): boolean {
    const roleCounts = this.users.reduce((acc, user: CustomUserModel) => {
      const roleName = user.role?.name || '';
      if (roleName in acc) {
        acc[roleName]++;
      } else {
        acc[roleName] = 1;
      }
      return acc;
    }, {});
    
    // Check moderator and regular observers
    const hasModAndObs = ((roleCounts['moderator'] || 0) + (roleCounts['moderator_external'] || 0)) === 1 &&
      (roleCounts['observer'] || 0) >= (this.settings?.min_observers || 0);
    
    // Check GEM observers only if hr_mandatory
    if (this.settings?.has_observer_gem === 'hr_mandatory') {
      return hasModAndObs && 
        (roleCounts['observer_gem'] || 0) >= (this.settings?.min_observers_gem || 0);
    }
    
    return hasModAndObs;
  }

}
